/**
 * Modal that shows when a teacher has reviewed student submissions.
 * Displays summary of passed/failed reviews and detailed feedback for each.
 *
 * Supports two modes:
 * - 'unseen': Auto-popup for new reviews (default)
 * - 'history': Manual open via bell icon, shows swipeable day-by-day view
 */

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { formatLessonLabel } from '../../../utils/lessonTitle';
import { formatDateYMD, getTodayInChina } from '../../../utils/timestampUtils';
import { Check, X, Volume2, Video, ChevronLeft, ChevronRight } from 'lucide-react';
import { getDisplayFeedback } from '../../../utils/smartFeedback';
import { trackTeacherFeedbackAudioPlayed } from '../../../utils/posthogEvents';
import { markFeedbackListened } from '../../../shared/api/submissions';
import { isVideoNote, TeacherVideoPlayer } from '../../../shared/teacherVideo';
import { playAudioBuffer } from '../../lessonPractice/utils/audioBufferPlayer';
import { getLevelForPoints } from '../../../config/levelsConfig';
import LevelSprite from './LevelSprite';
import { MarkedTranscript } from '../../../shared/markup/MarkedTranscript';
import { hasMarkup, parseMarkup } from '../../../shared/markup/transcriptMarkup';
import './ReviewFeedbackModal.css';

// Swipe detection threshold in pixels
const SWIPE_THRESHOLD = 50;

/**
 * Group reviews by date for history mode display
 */
function groupReviewsByDate(reviews) {
  const groups = {};
  const today = getTodayInChina();

  reviews.forEach(review => {
    const dateStr = formatDateYMD(review.created_at);
    if (!groups[dateStr]) {
      groups[dateStr] = {
        dateStr,
        isToday: dateStr === today,
        reviews: [],
      };
    }
    groups[dateStr].reviews.push(review);
  });

  // Convert to array sorted by date (newest first)
  return Object.values(groups).sort((a, b) => b.dateStr.localeCompare(a.dateStr));
}

/**
 * Format date for display in group header
 */
function formatDateHeader(dateStr, isToday) {
  if (isToday) return '今天';

  // Parse YYYY-MM-DD and format as M月D日
  const [, month, day] = dateStr.split('-');
  return `${parseInt(month, 10)}月${parseInt(day, 10)}日`;
}

/** How an assignment's outcome reads on its award row. */
function awardLabel(award) {
  if (award.perfect) return '满分通过 ⭐';
  if (award.is_rework) return '返工完成 🔄';
  return '完成作业';
}

/** The day's quiz sections as they appear in the mail (no section = a pre-split whole day). */
const QUIZ_SECTION_LABELS = { listening: '每日听力', readwrite: '每日读写', speaking: '每日口语' };

/** A green pass with nothing from the teacher tells the child nothing new. */
function worthShowing(review) {
  const s = review.submission;
  return review.rating !== 1 || !!getDisplayFeedback(s?.feedback) || !!s?.notes || hasMarkup(parseMarkup(s?.markup));
}

export function ReviewFeedbackModal({
  isOpen,
  mode = 'unseen',
  reviews,
  summary,
  assignments = [],
  quizzes = [],
  points = 0,
  lessonLabelCache,
  onClose,
}) {
  const modalRef = useRef(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [watching, setWatching] = useState(null); // { url, submissionId } of the teacher's video note open full-screen
  const handleRef = useRef(null);

  useEffect(() => () => handleRef.current?.stop(), []);

  // Day navigation state for history mode
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const touchStartRef = useRef(null);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Stop audio when modal closes
  useEffect(() => {
    if (!isOpen) {
      handleRef.current?.stop();
      setPlayingAudioId(null);
    }
  }, [isOpen]);

  // Close on click outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Play/stop audio feedback. Mark listened at the 90% threshold (more reliable
  // than onEnded on iOS Safari). The POST is fire-and-forget here because this
  // modal has no listen-gate to release — the badge for the teacher reads from
  // the server-stamped column on next teacher fetch.
  const stampListened = (submissionId) => {
    if (submissionId) markFeedbackListened(submissionId).catch(() => {});
  };

  const handlePlayAudio = (reviewId, audioUrl, submissionId) => {
    handleRef.current?.stop();

    if (playingAudioId === reviewId) {
      setPlayingAudioId(null);
      return;
    }

    setPlayingAudioId(reviewId);
    handleRef.current = playAudioBuffer(audioUrl, {
      onThreshold: () => stampListened(submissionId),
      onEnded: () => setPlayingAudioId(null),
      onError: () => setPlayingAudioId(null),
    });
  };

  // Group reviews by date for history mode
  const dateGroups = useMemo(() => {
    if (mode !== 'history' || !reviews?.length) return [];
    return groupReviewsByDate(reviews);
  }, [mode, reviews]);

  // Reset day index when modal opens or reviews change
  useEffect(() => {
    if (isOpen) {
      setCurrentDayIndex(0);
    }
  }, [isOpen, reviews]);

  // Navigation handlers
  const canGoBack = currentDayIndex > 0;
  const canGoForward = currentDayIndex < dateGroups.length - 1;

  const goToPreviousDay = useCallback(() => {
    if (canGoBack) {
      setCurrentDayIndex(prev => prev - 1);
    }
  }, [canGoBack]);

  const goToNextDay = useCallback(() => {
    if (canGoForward) {
      setCurrentDayIndex(prev => prev + 1);
    }
  }, [canGoForward]);

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;

    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };

    const deltaX = touchEnd.x - touchStartRef.current.x;
    const deltaY = touchEnd.y - touchStartRef.current.y;

    // Only trigger swipe if horizontal movement is greater than vertical
    // and exceeds threshold
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX > 0) {
        // Swipe right = go to more recent (previous index)
        goToPreviousDay();
      } else {
        // Swipe left = go to older (next index)
        goToNextDay();
      }
    }

    touchStartRef.current = null;
  }, [goToPreviousDay, goToNextDay]);

  // Keyboard navigation for desktop
  useEffect(() => {
    if (!isOpen || mode !== 'history') return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPreviousDay();
      } else if (e.key === 'ArrowRight') {
        goToNextDay();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, mode, goToPreviousDay, goToNextDay]);

  const isHistoryMode = mode === 'history';

  // Calculate summary - for history mode, show current day's stats
  // Must be before early return to maintain hooks order
  const currentDaySummary = useMemo(() => {
    if (!isHistoryMode || !dateGroups.length) return null;
    const dayReviews = dateGroups[currentDayIndex]?.reviews || [];
    const passed = dayReviews.filter(r => r.rating === 1).length;
    // A note on an early AI try (rating null) is neither.
    const failed = dayReviews.filter(r => r.rating === 0).length;
    return { passed, failed, total: passed + failed };
  }, [isHistoryMode, dateGroups, currentDayIndex]);

  if (!isOpen) return null;

  // Use per-day stats in history mode, overall stats in unseen mode
  const { passed = 0, failed = 0 } =
    isHistoryMode ? (currentDaySummary || {}) : (summary || {});

  const level = getLevelForPoints(points);
  const lessonKey = (topic, batch, n) => `${topic}|${batch}|${Number(n)}`;

  // Render a single review item
  // `nested` = under its lesson's award row, so the lesson label would only repeat it.
  const renderReviewItem = (review, nested = false) => {
    const { submission } = review;
    const isPassed = review.rating === 1;
    // A teacher's note on an early AI try: a note, not a pass or a fail.
    const isNote = review.rating == null;
    const tone = isNote ? 'note' : (isPassed ? 'passed' : 'failed');
    const lessonLabel = formatLessonLabel(
      submission?.topic,
      submission?.batch,
      submission?.lesson_number,
      lessonLabelCache
    );
    const hasAudioFeedback = !!submission?.notes;
    const hasVideoFeedback = isVideoNote(submission?.notes);
    const isPlaying = playingAudioId === review.id;

    return (
      <div
        key={review.id}
        className={`review-item ${tone}`}
      >
        <div className="review-item-header">
          <span className="review-item-label">
            <span className={`review-icon ${tone}`}>
              {isNote ? '💬' : isPassed ? '✓' : '✗'}
            </span>
            {!nested && <>{isNote ? ' ' : isPassed ? ' 📗 ' : ' 📕 '}{lessonLabel}</>}
          </span>
        </div>

        {/* A 总结's stored text is the whole page it retold, and an AI answer's is
            the question — so those say what they were instead. */}
        {submission?.submission_type === 'Summary' ? (
          <div className="review-item-text">总结（用英语讲故事）</div>
        ) : submission?.submission_type === 'AiTutor' ? (
          <div className="review-item-text">
            全英问答："{submission.sentence_text}"{submission.attempt_number && isNote ? ` · 第${submission.attempt_number}次` : ''}
          </div>
        ) : submission?.sentence_text && (
          <div className="review-item-text">
            "{submission.sentence_text}"
          </div>
        )}

        {/* 总结 / AI answer: what the child said, as the teacher corrected and marked it. */}
        {hasMarkup(parseMarkup(submission?.markup)) && (
          <div className="review-item-markup">
            <span className="feedback-label">你说的是：</span>
            <MarkedTranscript markup={submission.markup} />
          </div>
        )}

        {getDisplayFeedback(submission?.feedback) && (
          <div className="review-item-feedback">
            <span className="feedback-label">老师说:</span>
            <span className="feedback-text">"{getDisplayFeedback(submission.feedback)}"</span>
          </div>
        )}

        {hasVideoFeedback && (
          <button
            className="review-audio-btn"
            onClick={() => setWatching({ url: submission.notes, submissionId: review.submission_id })}
          >
            <Video size={16} />
            <span>看老师的视频反馈</span>
          </button>
        )}

        {hasAudioFeedback && !hasVideoFeedback && (
          <button
            className={`review-audio-btn ${isPlaying ? 'playing' : ''}`}
            onClick={() => {
              if (!isPlaying) {
                trackTeacherFeedbackAudioPlayed({
                  submissionId: submission?.id,
                  studentId: submission?.student_id,
                  course: submission?.topic,
                  book: submission?.batch,
                  lessonNumber: submission?.lesson_number,
                  source: 'review_modal',
                  mode,
                });
              }
              handlePlayAudio(review.id, submission.notes, review.submission_id);
            }}
          >
            <Volume2 size={16} />
            <span>{isPlaying ? '停止播放' : '播放语音反馈'}</span>
          </button>
        )}
      </div>
    );
  };

  /**
   * One mail: the child's animal with what it added, then each lesson's award row with
   * the items worth showing under it; items with no award row (self-study) list last.
   * The popup shows one batch, the history shows one day — same layout for both.
   */
  const renderMail = (mailReviews, mailAssignments, mailPassed, mailFailed, mailQuizzes = []) => {
    const mailPoints = mailAssignments.reduce((sum, a) => (a.completed ? sum + a.earned - a.lost : sum), 0)
      + mailQuizzes.reduce((sum, q) => sum + q.points, 0);
    // Accuracy counts the quiz answers alongside the homework items.
    const rightCount = mailPassed + mailQuizzes.reduce((sum, q) => sum + q.correct, 0);
    const totalCount = mailPassed + mailFailed + mailQuizzes.reduce((sum, q) => sum + q.answered, 0);
    const itemsByAward = new Map();
    const looseItems = [];
    mailReviews.filter(worthShowing).forEach((review) => {
      const sub = review.submission;
      const award = mailAssignments.find(a =>
        lessonKey(a.topic, a.batch, a.lesson_number) === lessonKey(sub?.topic, sub?.batch, sub?.lesson_number));
      if (!award) { looseItems.push(review); return; }
      if (!itemsByAward.has(award.assignment_id)) itemsByAward.set(award.assignment_id, []);
      itemsByAward.get(award.assignment_id).push(review);
    });

    return (
      <>
        {mailAssignments.some(a => a.completed) || mailQuizzes.length > 0 ? (
          <div className="review-points-hero">
            <LevelSprite level={level.level} icon={level.icon} name={level.name} />
            <div className="review-points-figures">
              <span className="review-points-total">
                {mailPoints >= 0 ? `+${mailPoints}` : `−${-mailPoints}`}
              </span>
              <span className="review-points-accuracy">{totalCount} 题对了 {rightCount} 题</span>
            </div>
          </div>
        ) : (
          <div className="review-metrics">
            <div className="review-metric passed">
              <span className="metric-number">{mailPassed}</span>
              <span className="metric-label">通过</span>
            </div>
            <div className="review-metric failed">
              <span className="metric-number">{mailFailed}</span>
              <span className="metric-label">需重做</span>
            </div>
          </div>
        )}

        {/* What each assignment was worth. Shown per assignment rather than as a
            running total so a child can see WHY a lesson paid 20 instead of 10 —
            that is the only place the 满分通过 rule becomes visible. */}
        {(mailAssignments.length > 0 || mailQuizzes.length > 0) && (
          <div className="review-awards">
            {mailAssignments.map((a) => (
              <div key={a.assignment_id} className="review-award">
                <div className={`review-award-row${a.perfect ? ' perfect' : ''}`}>
                  <span className="review-award-lesson">
                    {formatLessonLabel(a.topic, a.batch, a.lesson_number, lessonLabelCache)}
                  </span>
                  {!a.completed ? (
                    <span className="review-award-note">还没完成</span>
                  ) : (
                    <>
                      <span className="review-award-note">
                        {awardLabel(a)}
                        {a.pending && <span className="review-award-pending"> · 批改中</span>}
                      </span>
                      <span className="review-award-points">
                        +{a.earned}
                        {a.lost > 0 && <span className="review-award-lost"> −{a.lost}</span>}
                      </span>
                    </>
                  )}
                </div>
                {itemsByAward.has(a.assignment_id) && (
                  <div className="review-list review-award-items">
                    {itemsByAward.get(a.assignment_id).map(review => renderReviewItem(review, true))}
                  </div>
                )}
              </div>
            ))}
            {/* The daily quiz: +15 for the day, −1 per wrong answer (the points rule).
                每日口语 is a flat +10 — a miss costs nothing, so there is no −. */}
            {mailQuizzes.map((q) => (
              <div key={`quiz-${q.date}-${q.section || 'day'}`} className="review-award">
                {q.section === 'speaking' ? (
                  <div className="review-award-row quiz">
                    <span className="review-award-lesson">{QUIZ_SECTION_LABELS.speaking}</span>
                    <span className="review-award-note">说对了 {q.correct}/{q.answered}</span>
                    <span className="review-award-points">+{q.points}</span>
                  </div>
                ) : (
                  <div className="review-award-row quiz">
                    <span className="review-award-lesson">{QUIZ_SECTION_LABELS[q.section] || '每日复习'}</span>
                    <span className="review-award-note">{q.answered} 题对了 {q.correct} 题</span>
                    <span className="review-award-points">
                      +{q.points + (q.answered - q.correct)}
                      {q.answered > q.correct && <span className="review-award-lost"> −{q.answered - q.correct}</span>}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Only what needs redoing or carries a teacher's word */}
        {looseItems.length > 0 && (
          <div className="review-details">
            <div className="review-details-title">详细反馈:</div>
            <div className="review-list">
              {looseItems.map(review => renderReviewItem(review))}
            </div>
          </div>
        )}
      </>
    );
  };

  // History: the day on screen, and only the award rows for lessons reviewed that day.
  const dayReviews = isHistoryMode ? (dateGroups[currentDayIndex]?.reviews || []) : [];
  const dayKeys = new Set(dayReviews.map(r =>
    lessonKey(r.submission?.topic, r.submission?.batch, r.submission?.lesson_number)));
  const dayAssignments = isHistoryMode
    ? assignments.filter(a => dayKeys.has(lessonKey(a.topic, a.batch, a.lesson_number)))
    : [];

  return (
    <div className="review-modal-overlay" onClick={handleOverlayClick}>
      <div className="review-modal" ref={modalRef}>
        <div className="review-modal-header">
          <h3 className="review-modal-title">
            {isHistoryMode ? '批改记录' : '昨天作业情况'}
          </h3>
          <button className="review-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="review-modal-content">
          {!isHistoryMode && renderMail(reviews || [], assignments, passed, failed, quizzes)}

          {/* History mode: swipeable day-by-day view */}
          {isHistoryMode && dateGroups.length > 0 && (
            <div
              className="review-history-content"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Day navigation header */}
              <div className="review-day-nav">
                <button
                  className="review-nav-btn"
                  onClick={goToPreviousDay}
                  disabled={!canGoBack}
                  aria-label="更近一天"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="review-day-indicator">
                  <span className="review-day-date">
                    {formatDateHeader(dateGroups[currentDayIndex].dateStr, dateGroups[currentDayIndex].isToday)}
                  </span>
                  <span className="review-day-count">
                    ({currentDayIndex + 1}/{dateGroups.length})
                  </span>
                </div>
                <button
                  className="review-nav-btn"
                  onClick={goToNextDay}
                  disabled={!canGoForward}
                  aria-label="更早一天"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="review-day-content">
                {renderMail(dayReviews, dayAssignments, passed, failed)}
              </div>

              {/* Swipe hint for mobile */}
              <div className="review-swipe-hint">
                左右滑动切换日期
              </div>
            </div>
          )}

          {/* History mode: empty state */}
          {isHistoryMode && dateGroups.length === 0 && (
            <div className="review-empty-state">
              <p>暂无批改记录</p>
            </div>
          )}

        </div>

        <div className="review-modal-footer">
          <button className="review-dismiss-btn" onClick={onClose}>
            {isHistoryMode ? '关闭' : '知道了'}
          </button>
        </div>
      </div>
      {watching && (
        <TeacherVideoPlayer
          url={watching.url}
          onClose={() => setWatching(null)}
          onWatched={() => stampListened(watching.submissionId)}
        />
      )}
    </div>
  );
}

export default ReviewFeedbackModal;
